import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { FormArray, NgForm } from '@angular/forms';
import { OffersService } from '../services/offers.service';
import { LocationService } from '../../locations/services/location.service';
import { OfferTypeService } from '../../offer-types/services/offer-types.service';
import { TagsService } from '../../tags/services/tags.service';
import { OfferDetails, OfferType, emptyOfferDetails, ClickToBook, ReservationType, OfferTypeEnum, OfferContractPaymentPeriodEnum, SymbolResponse, OfferStatus } from '../interfaces/offer.interfaces';
import { SelectOption, getClickToBookLabels, getOfferTypeTypesLabels, getReservationTypeDropdown, getOfferContractPaymentPeriodLabels } from '../interfaces/offer.dropdowns';
import { emptyAvailabilitySlot } from '../interfaces/availability-slots.inferfaces';
import * as moment from 'moment';
import { Subscription } from 'rxjs/Subscription';
import { Title } from '@angular/platform-browser';
const alertify = require('alertify.js');

const NEW_OFFER_UNIT = 'weeks';
const NEW_OFFER_CUTOFF = 3;

@Component({
  selector: 'ch-offer-edit',
  templateUrl: './offer-edit.component.html',
  styleUrls: ['./offer-edit.component.css'],
})
export class OfferEditComponent implements OnInit, OnDestroy {
  public nothingSelectedMessage = 'Select Location';
  public offer: OfferDetails = emptyOfferDetails();
  public establishmentName: string;
  public isNew = false;
  public offerId: string;
  public dataLoaded = false;
  public formLocked = true;

  public initialDesription = '';
  public initialNewAppDescription = '';
  public initialNotes = '';

  public offerTypesDropdown: SelectOption<string>[] = [];
  public offerTagsDropdown: SelectOption<string>[] = [];

  public showSubscriptionModal: boolean;
  public availabilityFA: FormArray;
  public availability = [];

  public locations: any[] = [];

  private _selectedOfferTypes: SelectOption<string>[];
  private _selectedOfferTags: SelectOption<string>[];

  private offerTypesSubscripton$: Subscription;
  private offerTagsSubscripton$: Subscription;
  private offerSubscription$: Subscription;

  public priceDropdown: SelectOption<number>[] = [];
  public priceSymbol: string;

  public showOfferPreview = false;

  public statusToggleOptions: Array<SelectOption<OfferStatus>>;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private offerService: OffersService,
    private locationService: LocationService,
    private offerTypeService: OfferTypeService,
    private offerTagsService: TagsService,
    private titleService: Title,
  ) {
    alertify.logPosition('bottom right');
  }

  ngOnInit() {
    this.locationService.getLocationData().then((resp: any[]) => {
      this.locations = resp;
    });

    this.offerTypesSubscripton$ = this.offerTypeService
      .getOfferTypes()
      .map(resp => resp.json())
      .subscribe((types: OfferType[]) => {
        this.offerTypesDropdown = types.map(type => {
          return { label: type.name, value: type.id };
        });
      });

    this.offerTagsSubscripton$ = this.offerTagsService
      .getAllTags()
      .map(resp => resp.json())
      .subscribe((tags) => {
        this.offerTagsDropdown = tags.map(tag => {
          return {
            label: tag,
            value: tag
          }
        })
      })

    this.activatedRoute.queryParamMap
      .map((params: Params) => params.params)
      .subscribe(params => {
        const editId = params.editId;
        const duplicateId = params.duplicateId;
        if (duplicateId) {
          this.fetchData(duplicateId, true);
        } else if (editId) {
          this.offerId = editId;
          this.fetchData(editId);
        } else {
          this.isNew = true;
          this.dataLoaded = true;
          this.formLocked = false;
          this._selectedOfferTypes = [];
          this._selectedOfferTags = [];
          this.titleService.setTitle(`New Offer | CODE Hospitality CMS`);
        }
      });
  }

  ngOnDestroy() {
    if (this.offerSubscription$) {
      this.offerSubscription$.unsubscribe();
    }
    if (this.offerTypesSubscripton$) {
      this.offerTypesSubscripton$.unsubscribe();
    }
    if (this.offerTagsSubscripton$) {
      this.offerTagsSubscripton$.unsubscribe();
    }

    this.titleService.setTitle(`CODE Hospitality CMS`);
  }

  private fetchData(id: string, isDuplicate = false) {
    this.offerSubscription$ = this.offerService
      .getOffer(id)
      .map(resp => resp.json())
      .subscribe((offer: OfferDetails) => {
        this.offer = offer;
        this.titleService.setTitle(`${isDuplicate ? 'Duplicate' : this.offer.establishment.name} | Offers | CODE Hospitality CMS`);
        if (isDuplicate) {
          this.offer.startDate = null;
          this.offer.endDate = null;
          this.offer.status = OfferStatus.DRAFT;
        }
        delete this.offer.id;
        this.dataLoaded = true;
        this.formLocked = false;
        this.initialDesription = this.offer.description;
        this.initialNewAppDescription = this.offer.newAppDescription;
        this.initialNotes = this.offer.notes;
        this.establishmentName = offer.establishment.name;
        this.updateOfferPrices();
        this._selectedOfferTypes = this.offerTypesDropdown.filter(option => offer.offerTypes.includes(option.value));
        this._selectedOfferTags = this.offerTagsDropdown.filter(option =>
          offer.offerTags.sort((a, b) => {
            if (a.isPrimary === b.isPrimary) {
              // Equivalent to (a.isPrimary && b.isPrimary) || (!a.isPrimary && !b.isPrimary)
              return 0;
            } else if (a.isPrimary) {
              // Equivalent to (a.isPrimary && !b.isPrimary)
              return 1;
            } else {
              // Equivalent to (!a.isPrimary && b.isPrimary)
              return -1;
            }
          }).map(tag => tag.tagName).includes(option.value))
        switch (this.offer.status) {
          case OfferStatus.DRAFT:
            this.statusToggleOptions = [
              {
                label: 'Draft',
                value: OfferStatus.DRAFT
              },
              {
                label: 'Published',
                value: OfferStatus.PUBLISHED
              }
            ]
            break;
          case OfferStatus.PUBLISHED:
            this.statusToggleOptions = [
              {
                label: 'Published',
                value: OfferStatus.PUBLISHED
              },
              {
                label: 'Expired',
                value: OfferStatus.EXPIRED
              }
            ]
            break;
          case OfferStatus.EXPIRED:
            this.statusToggleOptions = [
              {
                label: 'Expired',
                value: OfferStatus.EXPIRED
              },
              {
                label: 'Draft',
                value: OfferStatus.DRAFT
              }
            ]
            break;
          default:
            this.statusToggleOptions = []
        }
      });
  }

  get changeStatusText(): string {
    if (!this.statusToggleOptions || this.statusToggleOptions.length == 0) return '';
    const otherStatusIndex = this.statusToggleOptions.findIndex(status => status.value != this.offer.status);
    let prefix: string;
    if (otherStatusIndex == 0) {
      prefix = 'Revert';
    } else {
      prefix = 'Change';
    }
    return `${prefix} status to ${this.statusToggleOptions[otherStatusIndex].label}`;
  }

  public changeStatus() {
    if (!this.statusToggleOptions || this.statusToggleOptions.length == 0) return;
    this.offer.status = this.statusToggleOptions.find(status => status.value != this.offer.status)!.value;
  }

  get isStatusDraft(): boolean {
    return this.offer.status == OfferStatus.DRAFT;
  }

  set selectedOfferTypes(value: string) {
    if (value === null) {
      return;
    }
    const added = this.offerTypesDropdown.find(option => option.value === value);
    if (this._selectedOfferTypes.includes(added)) {
      return;
    }
    this.offer.offerTypes.push(value);
    this._selectedOfferTypes.push(added);
  }

  get selectedOfferTypes() {
    return null;
  }


  public getSelectedOfferTypes(): SelectOption<string>[] {
    return this._selectedOfferTypes;
  }

  public removeOfferType(value: string) {
    this._selectedOfferTypes = this._selectedOfferTypes.filter(option => option.value !== value);
  }

  set selectedOfferTags(value: string) {
    if (value === null) {
      return;
    }
    if (this._selectedOfferTags.length >= 6) {
      return;
    }
    const added = this.offerTagsDropdown.find(option => option.value === value);
    if (this._selectedOfferTags.includes(added)) {
      return;
    }
    this.offer.offerTags.push({ tagName: value, isPrimary: false });
    this._selectedOfferTags.push(added);
  }

  get selectedOfferTags() {
    return null;
  }

  public getSelectedOfferTags(): SelectOption<string>[] {
    return this._selectedOfferTags;
  }

  public removeOfferTag(value: string) {
    this.offer.offerTags = this.offer.offerTags.filter(tag => tag.tagName !== value)
    this._selectedOfferTags = this._selectedOfferTags.filter(tag => tag.value !== value);
  }

  public submit(form: NgForm) {
    if (!form.valid) {
      Object.values(form.controls).forEach(control => {
        control.markAsTouched();
      });
      return;
    }

    this.formLocked = true;

    this.offer.offerTags.forEach((tag, index) => {
      tag.isPrimary = index < 3;
    })

    if (this.offerId) {
      this.updateOffer();
    } else {
      this.createOffer();
    }
  }

  private createOffer(): void {
    this.offerService.addOffer(this.offer).subscribe(
      (_: any) => {
        this.navigateToMainPage();
        this.formLocked = false;
      },
      (error: any) => {
        this.formLocked = false;
        this.alertError(error);
      }
    );
  }

  private updateOffer(): void {
    this.offer.offerTypes = this._selectedOfferTypes.map(type => type.value);
    this.offerService.updateOffer(this.offer, this.offerId).subscribe(
      (_: any) => {
        this.navigateToMainPage();
        this.formLocked = false;
      },
      (error: any) => {
        this.formLocked = false;
        this.alertError(error);
      }
    );
  }

  private alertError(error: any) {
    if (!error.error) {
      alertify.error(`There was a ${error.status} error with the request.`);
    } else {
      alertify.error(`There was a ${error.status} error with message '${error.error}'.`);
    }
  }

  public navigateToMainPage(): void {
    this.router.navigateByUrl('/admin/offers');
  }

  public locationChanged(value) {
    if (typeof value === 'string') {
      this.offer.establishment.locationId = value;
      this.updateOfferPrices();
    }
  }

  public imageChanged(newImageId: string) {
    this.offer.imageId = newImageId;
  }

  public startDateChange(newStartDate): void {
    this.offer.startDate = newStartDate ? moment(newStartDate).format('YYYY-MM-DD') : null;
  }

  public expiryDateChange(newEndDate): void {
    this.offer.endDate = newEndDate ? moment(newEndDate).format('YYYY-MM-DD') : null;
  }

  public contractStartDateChange(newDate): void {
    this.offer.contractStartDate = newDate ? moment(newDate).format('YYYY-MM-DD') : null;
  }

  public contractRenewalDateChange(newDate): void {
    this.offer.contractRenewalDate = newDate ? moment(newDate).format('YYYY-MM-DD') : null;
  }

  public delete() {
    if (confirm('Are you sure you want to delete offer?')) {
      this.offerService.deleteOffer(this.offerId).subscribe(_ => {
        this.navigateToMainPage();
      });
    }
  }

  public duplicateOffer() {
    this.router.navigate(['/admin/offers/add'], {
      queryParams: {
        duplicateId: this.offerId,
      },
    });
  }

  get clickToBookTypes(): SelectOption<ClickToBook>[] {
    return getClickToBookLabels;
  }

  get offerTypeTypes(): SelectOption<OfferTypeEnum>[] {
    return getOfferTypeTypesLabels;
  }

  get offerContractPaymentPeriods(): SelectOption<OfferContractPaymentPeriodEnum>[] {
    return getOfferContractPaymentPeriodLabels;
  }

  get reservationTypes(): SelectOption<ReservationType>[] {
    return getReservationTypeDropdown;
  }

  public redemptionUrlRequired(): boolean {
    return this.offer.offerType === OfferTypeEnum.LINK_BASED && this.offer.redemptionUrl === null;
  }

  public showRedemptionUrl(): boolean {
    return this.offer.offerType === OfferTypeEnum.LINK_BASED;
  }

  public phoneRequired(): boolean {
    return this.offer.clickToBook === ClickToBook.PHONE;
  }

  public emailRequired(): boolean {
    return this.offer.clickToBook === ClickToBook.EMAIL;
  }

  public bookingUrlRequired(): boolean {
    return this.offer.clickToBook === ClickToBook.WEBSITE;
  }

  public updateDescription(newDescription: string): void {
    this.offer.description = newDescription;
  }

  public updateNewAppDescription(newNewAppDescription: string): void {
    this.offer.newAppDescription = newNewAppDescription;
  }

  public updateNotes(newNotes: string): void {
    this.offer.notes = newNotes;
  }

  public markHasPartySize(checked) {
    this.offer.hasPartySize = checked;
  }

  public markFeatured(checked) {
    this.offer.featured = checked;
  }

  public clearDescription(): string {
    const description = this.offer.description;
    if (!description) {
      return null;
    } else {
      return description.replace(/<\/?[^>]+(>|$)/g, '');
    }
  }

  public clearNewAppDescription(): string {
    const newAppDescription = this.offer.newAppDescription;
    if (!newAppDescription) {
      return null;
    } else {
      return newAppDescription.replace(/<\/?[^>]+(>|$)/g, '');
    }
  }

  public addAvailabilitySlot(): void {
    const newSlot = emptyAvailabilitySlot();
    this.offer.offerAvailabilitySlots.push(newSlot);
  }

  public removeSlot(index: number): void {
    this.offer.offerAvailabilitySlots.splice(index, 1);
  }

  public validateSlots(): boolean {
    return this.offer.offerAvailabilitySlots.length > 0;
  }

  public slotName(index: number): string {
    return `availabilitySlot-${index}`;
  }

  private async updateOfferPrices() {
    if (!this.offer.establishment.locationId)
      return
    const location = await this.locationService.getLocation(this.offer.establishment.locationId).toPromise().then(res => res.json());
    this.priceDropdown = [1, 2, 3, 4].map(price => ({
      label: location.currencySign.repeat(price),
      value: price,
    }));
  }

  public showOfferPreviewDialog(): void {
    this.showOfferPreview = true;
  }

  public closeOfferPreviewDialog(): void {
    this.showOfferPreview = false;
  }

  get isOfferNew(): boolean {
    return moment().diff(moment(this.offer.changedAt), NEW_OFFER_UNIT, true) < NEW_OFFER_CUTOFF;
  }

  public toggleOfferNew(): void {
    this.offerService.updateOfferIsNew(this.offerId, !this.isOfferNew).subscribe(() => {
      this.navigateToMainPage();
    });
  }
}



// WEBPACK FOOTER //
// /Users/thxgg/Projects/Work/code-hospitality-cms/src/app/offers/offer-edit/offer-edit.component.ts